import Header from "../../../Layouts/Components/Header";
import React, { useEffect, useCallback, useState } from "react";
import { Grid, Box } from "@mui/material";
import CustomButton from "../../shared/CustomButton";

import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";

import useSnackBar from "../../Hooks/useSnackBar";

import AccountsTable from "./LicensesTable";
import LicenseDetails from "./LicenseDetails";
import ScreenLoader from "../../shared/ScreenLoader";
import {
  getLicenses,
  resetLicense,
  getModules,
  generateLicense,
  updateLicense,
  getCountries,
} from "./state/actions";
import NoLicenseSelected from "./NoLicenseSelected";
import SaveChangesModal from "../../shared/SaveChangesModal";
import * as actionTypes from "./state/actionTypes";

const Licensee = () => {
  const dispatch = useDispatch();
  const { success, error, snackbar } = useSnackBar();
  const loadLicenses = useCallback(
    () => dispatch(getLicenses(error)),
    [dispatch, error]
  );
  const loadModules = useCallback(
    () => dispatch(getModules(error)),
    [dispatch, error]
  );
  const loadCountries = useCallback(
    () => dispatch(getCountries(error)),
    [dispatch, error]
  );

  const reset = useCallback(() => dispatch(resetLicense()), [dispatch]);

  const [touched, setTouched] = useState(false);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingCurrentLicense, setPendingCurrentLicense] = useState(null);

  const {
    processing,
    loadingLicense,
    licenses,
    currentLicense,
    modules,
    countries,
  } = useSelector((state) => state.adminLicense);

  const dataConstructor = (values) => {
    let data;

    if (values.isNew) {
      data = {
        licenseName: values.licenseName,
        bankBicId: values.bankBicId,
        bankId: values.bankId,
        expiryDate: values.expiryDate,
        creationDate: values.creationDate,
        allowedUsers: values.allowedUsers,
        messages: values.messages,
        modules: values.modules.map((module) => ({
          id: module.id,
          isAllowed: module.isAllowed,
        })),
        countryId: values.countryId,
      };
    } else {
      data = {
        uniqueId: values.uniqueId,
        licenseName: values.licenseName,
        key: values.key,
        expiryDate: values.expiryDate,
        creationDate: values.creationDate,
        bankBicId: values.bankBicId,
        bankId: values.bankId,
        allowedUsers: values.allowedUsers,
        messages: values.messages,
        modules: values.modules.map((module) => ({
          id: module.module.id,
          isAllowed: module.isAllowed,
        })),
        countryId: values.countryId,
      };
    }

    return data;
  };
  //Form validation
  const validationSchema = yup.object().shape({
    licenseName: yup.string().required("This field is mandatory"),
    bankName: yup
      .string()
      .notOneOf(["SELECT_BANK"], "This field is mandatory")
      .required("This field is mandatory"),
    bankShortName: yup
      .string()
      .notOneOf(["SELECT_BANK"], "This field is mandatory")
      .required("This field is mandatory"),
    bankBic: yup
      .string()
      .notOneOf(["SELECT_BANK_BIC"], "This field is mandatory")
      .required("This field is mandatory"),
    allowedUsers: yup
      .number()
      .typeError("This field must be a number")
      .min(1, "Concurrent users must be greater than 0")
      .required("This field is mandatory"),
    messages: yup
      // .number("This field must be a number")
      .string("This field is mandatory")
      .notOneOf(["SELECT_MESSAGE_BAND"], "This field is mandatory")
      // .typeError("This field must be a number")
      .required("This field is mandatory"),
    countryId: yup
      .number("This field is mandatory")
      .notOneOf(["SELECT_COUNTRY"], "This field is mandatory")
      .typeError("This field is mandatory")
      .required("This field is mandatory"),
    expiryDate: yup.string().required("This field is mandatory"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      uniqueId: currentLicense?.uniqueId || "",
      licenseName: currentLicense?.licenseName || "",
      bankId: currentLicense?.bankId || "SELECT_BANK",
      bankName: currentLicense?.bankName || "SELECT_BANK",
      bankShortName: currentLicense?.bankShortName || "SELECT_BANK",
      bankBic: currentLicense?.bankBic || "SELECT_BANK_BIC",
      bankBicId: currentLicense?.bankBicId || "SELECT_BANK_BIC",
      allowedUsers: currentLicense?.allowedUsers || "",
      messages: currentLicense?.messages || "",
      countryId: currentLicense?.countryId || "SELECT_COUNTRY",
      modules: currentLicense?.modules || [],
      key: currentLicense?.key || "",
      keySimplified: currentLicense?.keySimplified || "",
      expiryDate: currentLicense?.expiryDate || "",
      formatedExpiryDate: currentLicense?.expiryDate
        ? new Date(currentLicense.expiryDate).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : null,
      creationDate: currentLicense?.creationDate || "",
      duration: { id: "CUSTOM", label: "Custom" },
      isNew: currentLicense?.isNew || false,
      status: currentLicense?.isNew ? null : currentLicense?.status,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const data = dataConstructor(values);

      if (values.isNew) {
        dispatch(generateLicense(data, success, error)).then(() => {
          dispatch(getLicenses("")).then(() => {
            if (pendingCurrentLicense)
              dispatch({
                type: actionTypes.SET_CURRENT_LICENSE,
                payload: pendingCurrentLicense,
              });
          });
        });
      } else {
        dispatch(updateLicense(data, success, error)).then(() => {
          dispatch(getLicenses("")).then(() => {
            if (pendingCurrentLicense)
              dispatch({
                type: actionTypes.SET_CURRENT_LICENSE,
                payload: pendingCurrentLicense,
              });
          });
        });
      }
      setPendingCurrentLicense(null);
      setIsConfirmationModalOpen(false);
      setTouched(false);
    },
  });

  const handleConfirmSaveChanges = () => {
    formik.handleSubmit();
  };

  const handleDiscardChanges = () => {
    dispatch({
      type: actionTypes.SET_CURRENT_LICENSE,
      payload: pendingCurrentLicense,
    });
    if (currentLicense.isNew) dispatch({ type: actionTypes.REMOVE_NEW });
    setPendingCurrentLicense(null);
    setIsConfirmationModalOpen(false);
    setTouched(false);
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    setPendingCurrentLicense(null);
  };

  useEffect(() => {
    loadLicenses();
    loadModules();
    loadCountries();
    // loadBanks("");
    return () => reset();
  }, [reset, loadLicenses, loadModules, loadCountries]);

  // console.log(formik.values);
  // console.log(formik.errors);
  return (
    <div>
      {snackbar}

      <SaveChangesModal
        open={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        processing={processing}
        discardChanges={handleDiscardChanges}
        saveChanges={handleConfirmSaveChanges}
      />

      <Header headLine="License Generation">
        <CustomButton
          text="Save Changes"
          type="update"
          disabled={!touched}
          processing={processing}
          action={formik.handleSubmit}
        />
      </Header>
      {licenses && modules && countries ? (
        <Grid container p={4} columnSpacing={2}>
          <Grid item xs={4}>
            <Box
              sx={{
                background: "#ffffff",
                borderRadius: "6px",
                border: "1px solid #E7E9EF",
                padding: "15px",
              }}
            >
              <AccountsTable
                licenses={licenses}
                currentLicense={currentLicense}
                formik={formik}
                touched={touched}
                setIsConfirmationModalOpen={setIsConfirmationModalOpen}
                setPendingCurrentLicense={setPendingCurrentLicense}
                error={error}
              />
            </Box>
          </Grid>
          <Grid item xs={7}>
            <Box
              sx={{
                background: "##E7E9EF",
                borderRadius: "6px",
                border: "1px solid #E7E9EF",
                minHeight: "600px",
                // padding: "15px",
              }}
            >
              {loadingLicense ? (
                <ScreenLoader />
              ) : !currentLicense ? (
                <NoLicenseSelected />
              ) : (
                <LicenseDetails
                  touched={touched}
                  setTouched={setTouched}
                  formik={formik}
                  modules={formik.values.modules}
                  countries={countries}
                  banks={currentLicense.banks}
                  success={success}
                  error={error}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      ) : (
        <ScreenLoader />
      )}
    </div>
  );
};

export default Licensee;
